<script lang="ts">
  import { PUBLIC_API_URL } from '$env/static/public';
  import { onMount } from 'svelte';
  import { Style, Avatar } from '@dicebear/core';
  import micah from '@dicebear/styles/micah.json' with { type: 'json' };
  import { goto } from '$app/navigation';

  const SEARCH_VCAT = 1000;
  const EPOCH_MS = Date.UTC(2000, 0, 1);

  const avatarStyle = new Style(micah);
  const avatars = new Map();

  function fmtDate(lrp: number | null) {
    if (!lrp) return '—';
    return new Date(EPOCH_MS + lrp * 900000).toISOString().slice(0, 7);
  }
  function fmtNum(n: number | null) {
    if (n == null) return '—';
    if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K';
    return String(n);
  }
  function videosPerWeek(videos: number, ch_created: number, sample_time_periods: number) {
    if (!videos || !ch_created || !sample_time_periods) return '—';
    const weeks = (sample_time_periods - ch_created) * 900000 / (7 * 86400000);
    return weeks > 0 ? (videos / weeks).toFixed(1) : '—';
  }
  function chThumbUrl(raw: string) {
    if (!raw) return '';
    return raw.startsWith('http') ? raw : `https://yt3.ggpht.com${raw}=s800-c-k-c0x00ffffff-no-rj`;
  }

  // --- Lightbox ---
  let lbOpen = $state(false);
  let lbSlides: {type: string; src: string}[] = $state([]);
  let lbIdx = $state(0);

  function lbOpenFn(slides: typeof lbSlides, idx: number) {
    lbSlides = slides; lbIdx = idx; lbOpen = true;
  }
  function lbClose() { lbOpen = false; }
  function lbStep(d: number) { lbIdx = (lbIdx + d + lbSlides.length) % lbSlides.length; }

  const galleries: Record<string, {ch: typeof lbSlides; vid: typeof lbSlides}> = {};

  // --- Data ---
  let outcomes: [number, string][] = $state([]);
  let sweeps: Record<number, number[]> = $state({});
  let catMap = new Map<number, string>();
  let tabs: {id: number; label: string}[] = $state([]);
  let activeTab = $state(0);

  interface TabState {
    page: number;
    loaded: boolean;
    channels: any[];
    unlabelled: Set<string>;
    status: string;
    labelled: Set<string>;
  }
  const tabState: Record<number, TabState> = $state({});

  // --- Search ---
  let searchInput = $state('');
  let suggestions: any[] = $state([]);
  let showSuggestions = $state(false);
  let suggestTimer: ReturnType<typeof setTimeout> | null = null;

  function extractSearchOptions(q: string) {
    const knownOptions = ['b'];
    const fullQ: string[] = [];
    const opts: Record<string, string> = {};
    q.split(' ').forEach(p => {
      const kv = p.split(':');
      if (knownOptions.includes(kv[0])) opts[kv[0]] = kv[1];
      else fullQ.push(p);
    });
    opts.q = fullQ.join(' ');
    return opts;
  }

  function searchUrl(page: number, catId: number) {
    const q = extractSearchOptions(searchInput.trim());
    const args: Record<string, string> = {
      q: q.q,
      cat: catId === SEARCH_VCAT ? '' : String(catId),
      page: String(page),
    };
    if (q.b) args.b = q.b;
    return `/samples?${new URLSearchParams(args)}`;
  }

  async function loadPage(catId: number, page: number) {
    const state = tabState[catId];
    state.page = page;
    state.status = 'Loading…';
    state.channels = [];
    try {
      const r = await authenticatedFetch(searchUrl(page, catId));
      if (!r || !r.ok) { state.status = `Error ${r.status}`; return; }
      const channels = await r.json();
      state.status = `${channels.length} channels — page ${page + 1}`;
      channels.forEach((ch: any) => buildGallery(ch));
      state.channels = channels;
      state.unlabelled = new Set(channels.map((x: any) => x.id));
      state.labelled = new Set();
    } catch (e: any) {
      state.status = `Failed: ${e.message}`;
    }
  }

  function buildGallery(ch: any) {
    const gid = `g_${ch.id.replace(/[^a-z0-9]/gi, '_')}`;
    const chThumb = chThumbUrl(ch.thumbnail);
    const uploads = ch.uploads || [];
    galleries[gid] = {
      ch: chThumb ? [{ type: 'image', src: chThumb }] : [],
      vid: [
        ...uploads.map((v: any) => ({ type: 'image', src: `https://i.ytimg.com/vi/${v[0]}/maxresdefault.jpg` })),
        ...uploads.map((v: any) => ({ type: 'video', src: `https://www.youtube.com/embed/${v[0]}?autoplay=1` })),
      ],
    };
    return gid;
  }

  async function setType(tabId: number, channelId: string, type: number, el: HTMLInputElement) {
    el.disabled = true;
    try {
      const r = await authenticatedFetch(`/review?channel=${encodeURIComponent(channelId)}&type=${type}`);
      if (r && r.ok) {
        tabState[tabId].unlabelled.delete(channelId);
        tabState[tabId].labelled = new Set([...tabState[tabId].labelled, channelId]);
      } else { alert(`Error ${r.status}`); el.checked = false; }
    } finally { el.disabled = false; }
  }

  async function sweep(el: HTMLButtonElement, tabId: number, to: number) {
    const state = tabState[tabId];
    const unlabelled = new Set(state.unlabelled);
    // optimistic UI
    state.labelled = new Set([...state.labelled, ...unlabelled]);
    state.unlabelled = new Set();

    // disable the inputs for all rows that are swept
    const rows = el.closest('.tab-pane').querySelectorAll('tr');
    rows.forEach(row => {
        if (unlabelled.has(row.dataset.channelId)) {
          const input = row.querySelector(`input[value="${to}"]`);
          if (input) {
            input.checked = true;
            input.disabled = true;
          }
        }
    });

    const r = await authenticatedFetch(`/review?type=${to}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ channels: [...unlabelled].join(',')})
    });
    rows.forEach(row => {
      if (unlabelled.has(row.dataset.channelId)) {
        const input = row.querySelector(`input[value="${to}"]`);
        if (input) {
          if (!r || !r.ok) {
            input.checked = false;
          }
          input.disabled = false;
        }
      }
    });

    if (!r || !r.ok) {
      state.unlabelled = unlabelled;
      state.labelled = new Set([...state.labelled].filter(x => !unlabelled.has(x)));
      if (r) alert(`Error ${r.status}`);
      else alert('fetch failed');
    }
  }

  async function doSearch() {
    showSuggestions = false;
    for (const k of Object.keys(tabState)) {
      tabState[+k].loaded = false;
      tabState[+k].page = 0;
    }
    await loadPage(activeTab, 0);
  }

  function onSearchInput() {
    if (suggestTimer) clearTimeout(suggestTimer);
    const q = searchInput.trim();
    if (q.length < 3) { showSuggestions = false; return; }
    suggestTimer = setTimeout(async () => {
      const r = await authenticatedFetch(searchUrl(0, SEARCH_VCAT));
      if (!r.ok) return;
      const items = await r.json();
      suggestions = items.slice(0, 10);
      showSuggestions = suggestions.length > 0;
    }, 300);
  }

  function pickSuggestion(ch: any) {
    searchInput = ch.channel || ch.name;
    showSuggestions = false;
    doSearch();
  }

  function filterByBrowser(browser: number) {
    const q = extractSearchOptions(searchInput);
    searchInput = `b:${browser} ${q.q || ''}`.trim();
    loadPage(activeTab, 0);
  }

  function getAvatar(seed) {
    const existing = avatars.get(seed);
    if (existing) return existing;
    const newav = new Avatar(avatarStyle, {seed, size: 32}).toDataUri();
    avatars.set(seed, newav);
    return newav;
  }

  async function switchTab(id: number) {
    activeTab = id;
    if (!tabState[id].loaded) {
      tabState[id].loaded = true;
      await loadPage(id, 0);
    }
  }

  async function authenticatedFetch(url: string, options: RequestInit = {
      credentials: 'include',
    }) {
      try {
        const response = await fetch(`${PUBLIC_API_URL}${url}`, options);

        if (response.status === 401) {
            // Redirect to login page
            window.location.href = `${PUBLIC_API_URL}/login/review-ui`;
            throw new Error('Unauthorized');
        }
        return response;
      } catch (err) {
        console.error("fetching:", err);
      }
      return undefined;
  }

  onMount(async () => {
    const r = await authenticatedFetch(`/cats`);
    if (!r || !r.ok) {
      alert(`Failed to load categories: ${r.status}`); return;
    }
    const cats = await r.json();
    outcomes = cats.outcomes;
    sweeps = cats.sweeps;
    const allCats = [...cats.statuses, { id: SEARCH_VCAT, label: 'Any' }];
    allCats.forEach((cat: any) => catMap.set(cat.id, cat.label));
    tabs = allCats;
    allCats.forEach((cat: any) => {
      tabState[cat.id] = { page: 0, loaded: false, channels: [], unlabelled: new Set(), labelled: new Set(), status: '' };
    });
    if (allCats.length) {
      activeTab = allCats[0].id;
      tabState[activeTab].loaded = true;
      await loadPage(activeTab, 0);
    }

    document.addEventListener('wheel', (e) => {
      const wrap = (e.target as Element).closest('.thumbs-wrap');
      if (wrap) { e.preventDefault(); wrap.scrollBy(e.deltaY, 0); }
    }, { passive: false });

    document.addEventListener('keydown', (e) => {
      if (!lbOpen) return;
      if (e.key === 'ArrowRight') lbStep(1);
      else if (e.key === 'ArrowLeft') lbStep(-1);
      else if (e.key === 'Escape') lbClose();
    });
  });
</script>

<!-- Lightbox -->
<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
{#if lbOpen}
<div id="img-lb" class="open" onclick={(e) => { if (e.target === e.currentTarget) lbClose(); }}>
  <button id="img-lb-close" onclick={lbClose}>&times;</button>
  <button id="img-lb-prev" onclick={() => lbStep(-1)}>&#8249;</button>
  {#if lbSlides[lbIdx]?.type === 'image'}
    <img id="img-lb-img" src={lbSlides[lbIdx].src} alt="">
  {:else if lbSlides[lbIdx]?.type === 'video'}
    <iframe src={lbSlides[lbIdx].src} style="width:90vw;height:calc(90vw*9/16);max-height:90vh;border:none;" title="video"></iframe>
  {/if}
  <button id="img-lb-next" onclick={() => lbStep(1)}>&#8250;</button>
</div>
{/if}

<div class="d-flex align-items-center gap-3 mb-3">
  <h4 class="mb-0">Deslop Review</h4>
  <div id="search-wrap" style="position:relative;display:inline-block;">
    <input
      type="search"
      class="form-control form-control-sm"
      placeholder="Channel Filter (handle, name, description)..."
      style="width:260px"
      autocomplete="off"
      bind:value={searchInput}
      onsearch={doSearch}
      oninput={onSearchInput}
      onkeydown={(e) => { if (e.key === 'Enter') doSearch(); }}
    >
    {#if showSuggestions}
    <div id="search-suggestions" style="display:block;">
      {#each suggestions as ch}
        <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
        <div class="suggestion" onclick={() => pickSuggestion(ch)}>
          {ch.name || ch.channel}{ch.channel ? ` (@${ch.channel})` : ''}
        </div>
      {/each}
    </div>
    {/if}
  </div>
  <button class="btn btn-sm btn-primary" onclick={doSearch}>Filter</button>
</div>

<!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
<div onclick={(e) => { if (!(e.target as Element).closest('#search-wrap')) showSuggestions = false; }}>

<ul class="nav nav-tabs">
  {#each tabs as cat}
    <li class="nav-item">
      <a
        class="nav-link{activeTab === cat.id ? ' active' : ''}"
        href="#tab-{cat.id}"
        onclick={(e) => { e.preventDefault(); switchTab(cat.id); }}
      >{cat.label}</a>
    </li>
  {/each}
</ul>

<div class="tab-content">
  {#each tabs as cat}
    {#if activeTab === cat.id}
    {@const state = tabState[cat.id]}
    <div class="tab-pane show active" id="tab-{cat.id}">
      <div class="pagination-bar">
        <button class="btn btn-sm btn-outline-secondary" disabled={state.page === 0}
          onclick={() => loadPage(cat.id, state.page - 1)}>&#8249; Prev</button>
        <span>Page {state.page + 1}</span>
        <button class="btn btn-sm btn-outline-secondary" disabled={state.channels.length < 100}
          onclick={() => loadPage(cat.id, state.page + 1)}>Next &#8250;</button>
        {#if sweeps[cat.id]}
          <div class="sweep-actions-bar">
            {#each sweeps[cat.id] as s}
              <button class="btn btn-sm btn-outline-secondary"
                onclick={(e) => sweep(e.currentTarget, cat.id, s)}>Sweep as {catMap.get(s)}</button>
            {/each}
          </div>
        {/if}
      </div>
      <div class="status-line">{state.status}</div>
      <table class="table table-hover table-sm">
        <thead><tr><th>#</th><th>Channel</th><th>Stats</th><th>Recent Videos</th><th>Type</th></tr></thead>
        <tbody>
          {#each state.channels as ch, i}
            {@const gid = `g_${ch.id.replace(/[^a-z0-9]/gi, '_')}`}
            {@const chThumb = chThumbUrl(ch.thumbnail)}
            {@const uploads = ch.uploads || []}
            <tr class={state.labelled.has(ch.id) ? 'table-success' : ''} data-channel-id={ch.id}>
              <td>{state.page * 100 + i + 1}</td>
              <td class="chtd">
                {#if chThumb}
                  <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                  <img class="ch-thumb mb-1" src={chThumb} alt="" onclick={() => lbOpenFn(galleries[gid].ch, 0)}>
                {/if}
                <div>
                  <a href="https://www.youtube.com/{ch.channel ? ('@' + ch.channel) : ('channel/' + ch.id)}" target="_deslop_channel">
                    <strong>{ch.name || ch.channel}</strong>
                  </a>
                </div>
                {#if ch.post_id}
                  <div class="post-id"><a href="https://www.youtube.com/post/{ch.post_id}" target="_blank">post</a></div>
                {/if}
                <small class="text-muted">{ch.channel}</small>
                {#if ch.browsers}
                  <div class="browsers">
                    {#each ch.browsers as b}
                      <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                      <span class="browser"><img onclick={() => filterByBrowser(b)} src="{getAvatar(b)}" data-bs-toggle="tooltip" title="Browser {b}"></span>
                    {/each}
                  </div>
                {/if}
              </td>
              <td>
                <div class="stats">
                  <div>📅 {fmtDate(ch.ch_created)}</div>
                  <div>🎦 {fmtNum(ch.videos)} videos</div>
                  <div>👥 {fmtNum(ch.subs)} subs</div>
                  <div>📈 {videosPerWeek(ch.videos, ch.ch_created, ch.sample_time_periods)}/wk</div>
                  <div>{catMap.get(ch.status)}</div>
                </div>
              </td>
              <td>
                {#if uploads.length}
                  <div class="thumbs-wrap">
                    {#each uploads as v, vi}
                      <div class="thumb-item{v.length > 6 ? (v[5] ? ' licensed' : ' unlicensed') : ''}{v.length > 7 ? (v[7] ? ' hasPlacements' : ' noPlacements') : ''}">
                        <!-- svelte-ignore a11y_click_events_have_key_events a11y_no_static_element_interactions -->
                        <img src="https://i.ytimg.com/vi/{v[0]}/mqdefault.jpg" alt="" loading="lazy"
                          onclick={() => lbOpenFn(galleries[gid].vid, vi)}>
                        <div class="thumb-title">
                          <a target="_deslop_player" href="https://www.youtube.com/watch?v={v[0]}">{v[1]}</a>
                        </div>
                      </div>
                    {/each}
                  </div>
                {:else}
                  <span class="text-muted">—</span>
                {/if}
              </td>
              <td>
                <div class="type-radios">
                  {#each outcomes as [val, label]}
                    <label class="d-block">
                      <input class="type-radio" type="radio" name="type_{ch.id.replace(/[^a-z0-9]/gi, '_')}" value={val}
                        onchange={(e) => setType(cat.id, ch.id, val, e.currentTarget)}>
                      {label}
                    </label>
                  {/each}
                </div>
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
    {/if}
  {/each}
</div>

</div>

<style>
  :global(#img-lb) { display:none; position:fixed; inset:0; z-index:9999; background:rgba(0,0,0,.92);
            align-items:center; justify-content:center; cursor:zoom-out; }
  :global(#img-lb.open) { display:flex; }
  :global(#img-lb img) { max-width:100vw; max-height:100vh; width:auto; height:auto;
                object-fit:contain; box-shadow:0 0 40px #000; }
  :global(#img-lb-prev), :global(#img-lb-next) {
    position:absolute; top:50%; transform:translateY(-50%);
    background:rgba(255,255,255,.15); border:none; color:#fff;
    font-size:2rem; padding:0.2em 0.6em; cursor:pointer; user-select:none; z-index:1;
  }
  :global(#img-lb-prev) { left:0; } :global(#img-lb-next) { right:0; }
  :global(#img-lb-close) { position:absolute; top:8px; right:12px; background:none; border:none;
                  color:#fff; font-size:1.8rem; cursor:pointer; }
  :global(body) { padding: 16px; }
  :global(td) { vertical-align: middle !important; }
  .chtd { width: 200px; }
  :global(.thumbs-wrap) { display: flex; gap: 6px; overflow-x: auto; max-width: 1160px; padding-bottom: 4px; }
  :global(.thumb-item)  { flex: 0 0 auto; width: 200px; font-size: 11px; line-height: 1.3; }
  :global(.thumb-item img) { width: 200px; height: 120px; object-fit: cover; cursor: zoom-in; }
  :global(.licensed img) { border: 3px solid #44890b; border-top-right-radius: 10%;}
  :global(.unlicensed img) { border: 4px solid #fae96b; border-top-right-radius: 15%;}
  :global(.hasPlacements img) { border-top-left-radius: 15%;}
  :global(.thumb-title) { margin-top: 2px; max-height: 2.6em; overflow: hidden; }
  :global(.thumb-views) { color: #888; }
  :global(.ch-thumb) { cursor: zoom-in; width: 88px; height: 88px; object-fit: cover; border-radius: 50%; }
  :global(.stats) { font-size: 12px; line-height: 1.9; white-space: nowrap; }
  :global(.type-radios label) { display: block; margin: 1px 0; white-space: nowrap; }
  :global(.status-line) { color: #6c757d; margin-bottom: 8px; }
  :global(.tab-content) { padding-top: 12px; }
  :global(.pagination-bar) { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
  :global(.d-block) { font-size: small; height: 16px; }
  #search-suggestions { position:absolute; top:100%; left:0; right:0; z-index:1000;
    background:#fff; border:1px solid #ccc; border-top:none; max-height:240px;
    overflow-y:auto; }
  :global(#search-suggestions .suggestion) { padding:6px 10px; cursor:pointer; font-size:13px; }
  :global(#search-suggestions .suggestion:hover) { background:#f0f0f0; }
</style>
